import apiClientService from "./ApiClientService";

export function getPracticeTicketIdRange() {
   const start = Number(
      process.env.REACT_APP_TICKETS_START ||
         process.env.VITE_TICKETS_START ||
         246,
   );
   const count = Number(
      process.env.REACT_APP_TICKETS_COUNT ||
         process.env.VITE_TICKETS_COUNT ||
         24,
   );
   const safeCount = Number.isInteger(count) && count > 0 ? count : 24;
   const safeStart = Number.isInteger(start) && start > 0 ? start : 246;

   return {
      start: safeStart,
      end: safeStart + safeCount - 1,
      count: safeCount,
   };
}

export function isPracticeTheoryTicketId(id, range = getPracticeTicketIdRange()) {
   const ticketId = Number(id);
   return (
      Number.isInteger(ticketId) &&
      ticketId >= range.start &&
      ticketId <= range.end
   );
}

export function filterPracticeTheoryTickets(
   tickets = [],
   range = getPracticeTicketIdRange(),
) {
   return tickets.filter((ticket) => isPracticeTheoryTicketId(ticket?.id, range));
}

function debugPublishCall(fnName, method, endpoint, payload) {
   // Requested debug trace: show payload for every publish function.
}

function readItems(raw, fallbackKeys = []) {
   if (Array.isArray(raw)) return raw;
   if (!raw || typeof raw !== "object") return [];

   const candidates = ["data", "items", "results", "rows", ...fallbackKeys];
   for (const key of candidates) {
      const val = raw?.[key];
      if (Array.isArray(val)) return val;
   }
   return [];
}

function readPagination(raw, limit, itemsCount = 0) {
   const src = raw?.pagination || raw?.meta || raw || {};
   const totalPagesRaw =
      src?.totalPages ?? src?.pages ?? raw?.totalPages ?? raw?.pages;
   const totalRaw = src?.total ?? src?.count ?? raw?.total ?? raw?.count;

   const totalPages = Number(totalPagesRaw);
   const total = Number(totalRaw);

   if (Number.isInteger(totalPages) && totalPages > 0) {
      return {
         totalPages,
         total: Number.isFinite(total) ? total : null,
      };
   }

   if (Number.isFinite(total) && total >= 0 && Number(limit) > 0) {
      return {
         totalPages: Math.max(1, Math.ceil(total / Number(limit))),
         total,
      };
   }

   return {
      totalPages: itemsCount < Number(limit) ? 1 : null,
      total: Number.isFinite(total) ? total : null,
   };
}

async function parseJsonSafe(res, fallback = null) {
   const ct = (res.headers?.get?.("content-type") || "").toLowerCase();
   if (ct.includes("application/json")) {
      try {
         return await res.json();
      } catch {
         return fallback;
      }
   }

   const text = await res.text().catch(() => "");
   if (!text) return fallback;
   try {
      return JSON.parse(text);
   } catch {
      return fallback ?? text;
   }
}

async function throwApiError(prefix, res) {
   const text = await res.text().catch(() => "");
   throw new Error(`${prefix} ${res.status}: ${text || res.statusText}`);
}

function encodeId(id, fnName) {
   const n = Number(id);
   if (!Number.isInteger(n) || n <= 0) {
      throw new Error(`${fnName}: id invalid.`);
   }
   return encodeURIComponent(String(n));
}

/* ============================================================================
   QUESTIONS
============================================================================ */
export async function createExamQuestion(payload) {
   debugPublishCall("createExamQuestion", "POST", "/exams/questions", payload || {});
   const res = await apiClientService.post(
      "/exams/questions",
      JSON.stringify(payload || {}),
      "application/json; charset=UTF-8"
   );
   if (!res.ok && res.status !== 201) {
      await throwApiError("createExamQuestion", res);
   }
   return await parseJsonSafe(res, null);
}

export async function getExamQuestions({ page = 1, limit = 20 } = {}) {
   const params = new URLSearchParams();
   params.set("page", String(page ?? 1));
   params.set("limit", String(limit ?? 20));

   const res = await apiClientService.get(`/exams/questions?${params.toString()}`);
   if (!res.ok) {
      await throwApiError("getExamQuestions", res);
   }

   const raw = await parseJsonSafe(res, []);
   const items = readItems(raw, ["questions"]);
   return {
      raw,
      items,
      pagination: readPagination(raw, limit, items.length),
   };
}

export async function getExamQuestionById(id) {
   const qid = encodeId(id, "getExamQuestionById");
   const res = await apiClientService.get(`/exams/questions/${qid}`);
   if (!res.ok) {
      await throwApiError("getExamQuestionById", res);
   }
   return await parseJsonSafe(res, null);
}

export async function updateExamQuestion(id, payload) {
   const qid = encodeId(id, "updateExamQuestion");
   debugPublishCall(
      "updateExamQuestion",
      "PUT",
      `/exams/questions/${qid}`,
      payload || {}
   );
   const res = await apiClientService.put(
      `/exams/questions/${qid}`,
      JSON.stringify(payload || {}),
      "application/json; charset=UTF-8"
   );
   if (!res.ok) {
      await throwApiError("updateExamQuestion", res);
   }
   return await parseJsonSafe(res, null);
}

export async function deleteExamQuestion(id) {
   const qid = encodeId(id, "deleteExamQuestion");
   debugPublishCall("deleteExamQuestion", "DELETE", `/exams/questions/${qid}`, {
      id: Number(id),
   });
   const res = await apiClientService.delete(`/exams/questions/${qid}`);
   if (!res.ok && res.status !== 204) {
      await throwApiError("deleteExamQuestion", res);
   }
   return await parseJsonSafe(res, { id: Number(id) });
}

/* ============================================================================
   TICKETS
============================================================================ */
export async function createExamTicket(payload) {
   debugPublishCall("createExamTicket", "POST", "/exams/tickets", payload || {});
   const res = await apiClientService.post(
      "/exams/tickets",
      JSON.stringify(payload || {}),
      "application/json; charset=UTF-8"
   );
   if (!res.ok && res.status !== 201) {
      await throwApiError("createExamTicket", res);
   }
   return await parseJsonSafe(res, null);
}

export async function getExamTickets({
   page = 1,
   limit = 20,
   type = "EXAM",
} = {}) {
   const params = new URLSearchParams();
   params.set("page", String(page ?? 1));
   params.set("limit", String(limit ?? 20));
   if (type) params.set("type", String(type));

   const res = await apiClientService.get(`/exams/tickets?${params.toString()}`);
   if (!res.ok) {
      await throwApiError("getExamTickets", res);
   }

   const raw = await parseJsonSafe(res, []);
   const items = readItems(raw, ["tickets"]);
   return {
      raw,
      items,
      pagination: readPagination(raw, limit, items.length),
   };
}

export async function getExamTicketById(id) {
   const tid = encodeId(id, "getExamTicketById");
   const res = await apiClientService.get(`/exams/tickets/${tid}`);
   if (!res.ok) {
      await throwApiError("getExamTicketById", res);
   }
   return await parseJsonSafe(res, null);
}

export async function updateExamTicket(id, payload) {
   const tid = encodeId(id, "updateExamTicket");
   debugPublishCall("updateExamTicket", "PUT", `/exams/tickets/${tid}`, payload || {});
   const res = await apiClientService.put(
      `/exams/tickets/${tid}`,
      JSON.stringify(payload || {}),
      "application/json; charset=UTF-8"
   );
   if (!res.ok) {
      await throwApiError("updateExamTicket", res);
   }
   return await parseJsonSafe(res, null);
}

export async function deleteExamTicket(id) {
   const tid = encodeId(id, "deleteExamTicket");
   debugPublishCall("deleteExamTicket", "DELETE", `/exams/tickets/${tid}`, {
      id: Number(id),
   });
   const res = await apiClientService.delete(`/exams/tickets/${tid}`);
   if (!res.ok && res.status !== 204) {
      await throwApiError("deleteExamTicket", res);
   }
   return await parseJsonSafe(res, { id: Number(id) });
}

export async function getExamTicketQuestions(id) {
   const tid = encodeId(id, "getExamTicketQuestions");
   const res = await apiClientService.get(`/exams/tickets/${tid}/questions`);
   if (!res.ok) {
      await throwApiError("getExamTicketQuestions", res);
   }

   const raw = await parseJsonSafe(res, []);
   const items = readItems(raw, ["questions"]);
   return { raw, items };
}

export async function loadAllPracticeTickets({ pageSize = 100 } = {}) {
   const types = ["PRACTICE", "practice", "THEORY", "theory"];
   const collected = [];

   for (const type of types) {
      let page = 1;
      let guard = 0;

      while (guard < 200) {
         const response = await getExamTickets({
            page,
            limit: pageSize,
            type,
         });
         const rows = readItems(response?.raw ?? response, ["tickets"]);
         collected.push(...rows);

         const totalPages = Number(response?.pagination?.totalPages);
         if (Number.isInteger(totalPages) && totalPages > 0) {
            if (page >= totalPages) break;
         } else if (rows.length < pageSize) {
            break;
         }

         page += 1;
         guard += 1;
      }

      if (collected.length) break;
   }

   const byId = new Map();
   for (const row of collected) {
      const id = Number(row?.id);
      if (!Number.isInteger(id) || id <= 0 || byId.has(id)) continue;
      byId.set(id, row);
   }

   const normalized = Array.from(byId.values()).sort(
      (left, right) => Number(left.id) - Number(right.id),
   );

   const inRange = filterPracticeTheoryTickets(normalized);
   if (inRange.length) return inRange;

   if (normalized.length) return normalized;

   const { start, count } = getPracticeTicketIdRange();

   return Array.from({ length: Math.max(0, count) }, (_, index) => ({
      id: start + index,
      name: `P${index + 1}`,
      type: "PRACTICE",
   }));
}

async function mapWithConcurrency(items, limit, mapper) {
   const results = new Array(items.length);
   let cursor = 0;

   async function worker() {
      while (cursor < items.length) {
         const index = cursor;
         cursor += 1;
         results[index] = await mapper(items[index], index);
      }
   }

   const workers = Array.from(
      { length: Math.min(limit, items.length) },
      () => worker(),
   );
   await Promise.all(workers);
   return results;
}

function dedupeTicketsById(rows = []) {
   const byId = new Map();
   for (const row of rows) {
      const id = Number(row?.id);
      if (!Number.isInteger(id) || id <= 0 || byId.has(id)) continue;
      byId.set(id, row);
   }
   return Array.from(byId.values()).sort(
      (left, right) => Number(left.id) - Number(right.id),
   );
}

async function loadAllExamTicketsByType({
   type = "EXAM",
   pageSize = 100,
   maxPages = 500,
} = {}) {
   const collected = [];
   const listPages = [];
   let page = 1;

   while (page <= maxPages) {
      const response = await getExamTickets({
         page,
         limit: pageSize,
         type,
      });

      listPages.push(response.raw);
      collected.push(...response.items);

      const totalPages = Number(response?.pagination?.totalPages);
      if (Number.isInteger(totalPages) && totalPages > 0) {
         if (page >= totalPages) break;
      } else if (response.items.length < pageSize) {
         break;
      }

      page += 1;
   }

   return {
      tickets: dedupeTicketsById(collected),
      listPages,
   };
}

export async function buildExamTicketsExportBundle({
   ticketType = "EXAM",
   pageSize = 100,
   concurrency = 6,
   onProgress,
} = {}) {
   const normalizedType = String(ticketType || "EXAM").toUpperCase();
   const isPractice = normalizedType === "PRACTICE" || normalizedType === "THEORY";

   let tickets = [];
   let ticketsList = null;

   if (isPractice) {
      tickets = filterPracticeTheoryTickets(
         await loadAllPracticeTickets({ pageSize }),
      );
      ticketsList = { tickets, idRange: getPracticeTicketIdRange() };
   } else {
      const loaded = await loadAllExamTicketsByType({
         type: normalizedType,
         pageSize,
      });
      tickets = loaded.tickets;
      ticketsList =
         loaded.listPages.length === 1
            ? loaded.listPages[0]
            : { pages: loaded.listPages };
   }

   if (!tickets.length) {
      throw new Error("Nu am găsit bilete de exportat.");
   }

   let completed = 0;
   const ticketBundles = await mapWithConcurrency(
      tickets,
      concurrency,
      async (summary) => {
         const ticketId = Number(summary?.id);
         const [ticketDetail, questionsResponse] = await Promise.all([
            getExamTicketById(ticketId),
            getExamTicketQuestions(ticketId),
         ]);

         completed += 1;
         onProgress?.({
            current: completed,
            total: tickets.length,
            ticketId,
            ticketName: summary?.name || summary?.code || "",
         });

         return {
            ticketId,
            ticketName: summary?.name || summary?.code || null,
            ticketType: summary?.type || normalizedType,
            listItem: summary,
            ticket: ticketDetail,
            questions: questionsResponse.raw,
         };
      },
   );

   const questionsTotal = ticketBundles.reduce((sum, entry) => {
      const count = readItems(entry?.questions, ["questions"]).length;
      return sum + count;
   }, 0);

   return {
      exportedAt: new Date().toISOString(),
      ticketType: isPractice ? "PRACTICE" : normalizedType,
      idRange: isPractice ? getPracticeTicketIdRange() : null,
      summary: {
         ticketsTotal: ticketBundles.length,
         questionsTotal,
      },
      ticketsList,
      tickets: ticketBundles,
   };
}

export function downloadJsonExport(data, filename) {
   const stamp = new Date().toISOString().slice(0, 10);
   const safeName =
      String(filename || "").trim() ||
      `bilete-export-${stamp}.json`;
   const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json;charset=utf-8",
   });
   const href = URL.createObjectURL(blob);
   const anchor = document.createElement("a");
   anchor.href = href;
   anchor.download = safeName;
   document.body.appendChild(anchor);
   anchor.click();
   URL.revokeObjectURL(href);
   anchor.remove();
}
